import { useState, useCallback } from 'react'
import * as taskApi from 'api/taskApi'
import { TaskStatusFilter } from 'constants/index'
import { useParams } from 'react-router-dom'
import { createPickerTask } from 'api/taskApi'
import {
  CreateTaskPayload,
  TaskFetchParams,
  TaskCancelParams,
  TaskUpdatePayload,
  UITask
} from 'types/task'

function normalizeOpenTasks(list: any[]): UITask[] {
  return list as UITask[]
}

function normalizeFinishedTasks(list: any[]): UITask[] {
  return (list || []).map(t => {
    const destCode = t?.destinationBin?.binCode
    const srcCode = t?.sourceBin?.binCode
    const sourceBins = srcCode
      ? [{ bin: { binCode: srcCode }, quantity: t?.quantity }]
      : []

    return {
      ...t,
      destinationBinCode: destCode,
      sourceBins
    } as UITask
  })
}

function isFinishedStatus(s?: string) {
  const up = (s || '').toUpperCase()
  return up === 'COMPLETED' || up === 'CANCELED'
}

export const useTask = () => {
  const [tasks, setTasks] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const getTaskErrorMessage = (
    source: any,
    fallback: string
  ): string => {
    const data = source?.response?.data ?? source
    const msg = data?.message || data?.error || data?.errorCode
    if (typeof msg === 'string' && msg.trim()) {
      if (msg === 'TASK_DUPLICATE') {
        return 'This task has already been created. Please do not create it again.'
      }
      return msg
    }
    if (typeof source?.message === 'string' && source.message.trim()) {
      return source.message
    }
    return fallback
  }

  const warehouseID = useParams().warehouseID as string

  const [isPaginated, setIsPaginated] = useState(false)
  const [pagination, setPagination] = useState<{
    page: number
    pageSize: number
    total: number
  } | null>(null)

  const fetchOpenTasks = useCallback(
    async ({ warehouseID, status, keyword }: TaskFetchParams) => {
      try {
        setIsLoading(true)
        setError(null)

        const up = (status || '').toUpperCase()
        const effectiveStatus =
          up === 'OUT_OF_STOCK' ? TaskStatusFilter.PENDING : up

        const resp = await taskApi.fetchTasks({
          warehouseID,
          status: effectiveStatus,
          keyword
        })
        setTasks(normalizeOpenTasks(resp.tasks || []))
        setIsPaginated(false)
        setPagination(null)
      } catch (e: any) {
        console.error('fetchOpenTasks failed:', e)
        setError(e?.message || 'Failed to fetch tasks')
      } finally {
        setIsLoading(false)
      }
    },
    []
  )

  const fetchFinishedTasksPaged = useCallback(
    async ({
      warehouseID,
      status,
      keyword,
      page = 1,
      pageSize = 10
    }: TaskFetchParams) => {
      try {
        setIsLoading(true)
        setError(null)

        const up = (status || '').toUpperCase() as 'COMPLETED' | 'CANCELED'
        const resp = await taskApi.fetchFinishedTasks({
          warehouseID,
          status: up,
          page,
          pageSize,
          keyword
        })
        setTasks(normalizeFinishedTasks(resp.data || []))
        setIsPaginated(true)
        setPagination({ page, pageSize, total: resp.total ?? 0 })
      } catch (e: any) {
        console.error('fetchFinishedTasksPaged failed:', e)
        setError(e?.message || 'Failed to fetch finished tasks')
      } finally {
        setIsLoading(false)
      }
    },
    []
  )

  const fetchTasks = useCallback(
    async ({
      warehouseID,
      status,
      keyword,
      page,
      pageSize
    }: TaskFetchParams) => {
      const finished = isFinishedStatus(status)
      if (finished) {
        return fetchFinishedTasksPaged({
          warehouseID,
          status,
          keyword,
          page,
          pageSize
        })
      }
      return fetchOpenTasks({ warehouseID, status, keyword })
    },
    [fetchFinishedTasksPaged, fetchOpenTasks]
  )

  const cancelTask = async (taskID: string, params: TaskCancelParams) => {
    try {
      await taskApi.cancelTask(taskID)
      return { success: true as const }
    } catch (error) {
      console.error('❌ Failed to cancel task:', error)
      return {
        success: false as const,
        message: 'Failed to cancel task.'
      }
    }
  }

  const declineCancelRequest = async (taskID: string) => {
    try {
      await taskApi.declineCancelRequest(taskID)
      return { success: true as const }
    } catch (err: any) {
      console.error('Decline cancel request failed:', err)
      return {
        success: false as const,
        message:
          err?.response?.data?.message ||
          err?.response?.data?.errorCode ||
          err?.message ||
          'Decline failed'
      }
    }
  }

  const createTask = async (payload: CreateTaskPayload) => {
    try {
      setIsLoading(true)
      setError(null)
      const result = await taskApi.createTask({
        ...payload,
        warehouseID
      })

      if (!result.success) {
        const msg = getTaskErrorMessage(result, '❌ Task creation failed')
        setError(msg)
        return null
      }

      return result
    } catch (err: any) {
      const message = getTaskErrorMessage(err, '❌ Unknown error')
      setError(message)
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const createPickTask = async (
    productCode: string,
    quantity: number,
    destinationBinCode: string
  ): Promise<CreateTaskPayload | null> => {
    setIsLoading(true)
    setError(null)
    try {
      const result = await createPickerTask({
        productCode,
        quantity,
        warehouseID,
        destinationBinCode
      })

      if (!result.success) {
        const msg = getTaskErrorMessage(
          result,
          '❌ Pick task creation failed'
        )
        setError(msg)
        return null
      }

      return result.task
    } catch (err: any) {
      const message = getTaskErrorMessage(
        err,
        '❌ Failed to create task'
      )
      setError(message)
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const updateTask = async (
    taskID: string,
    payload: TaskUpdatePayload,
    _params?: TaskCancelParams
  ) => {
    try {
      await taskApi.updateTask(taskID, payload)
      return { success: true as const }
    } catch (err: any) {
      console.error('Update task failed:', err)
      return {
        success: false as const,
        message: err?.response?.data?.message || err?.message || 'Update failed'
      }
    }
  }

  return {
    tasks,
    isLoading,
    error,
    setError,
    cancelTask,
    declineCancelRequest,
    fetchTasks,
    createTask,
    createPickTask,
    updateTask,
    isPaginated,
    pagination,
    fetchOpenTasks,
    fetchFinishedTasksPaged
  }
}
